import React, { useState, useEffect } from 'react';
import { X, Save, Dice6, Eye, EyeOff, CreditCard, FileText, Mail, Phone, User, Calendar, Shield } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface EditUserModalUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleName?: string;
  subscriptionType?: 'free' | 'standard' | 'premium';
  subscriptionEndDate?: string | null;
  subscriptionStatus?: 'active' | 'trial_active' | 'charge_failed' | 'expired' | 'cancelled' | null;
}

interface Role {
  id: number;
  name: string;
  description?: string;
}

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: EditUserModalUser | null;
  onSuccess?: () => void;
}

// Генератор случайного пароля (8 символов)
function generatePassword(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 8; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, user, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'subscription' | 'stats'>('info');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    subscriptionType: 'free' as 'free' | 'standard' | 'premium',
    subscriptionEndDate: '',
    subscriptionStatus: 'active' as 'active' | 'expired' | 'cancelled',
    autoRenew: false
  });
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [resumeCount, setResumeCount] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [loadingRoles, setLoadingRoles] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      // Сначала загружаем роли, потом данные пользователя (чтобы роли были доступны при установке selectedRoleId)
      loadRoles().then(() => {
        loadUserData();
      });
    }
  }, [isOpen, user]);

  // Обновляем роль когда роли загружены
  useEffect(() => {
    if (roles.length > 0 && user && !selectedRoleId) {
      if (user.roleName) {
        const role = roles.find(r => r.name === user.roleName);
        if (role) {
          setSelectedRoleId(role.id);
        }
      }
    }
  }, [roles, user]);

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await api.get('/admin/roles');
      if (response.data && response.data.success && Array.isArray(response.data.roles)) {
        setRoles(response.data.roles);
      }
    } catch (error) {
      console.error('Ошибка загрузки ролей:', error);
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadUserData = async () => {
    if (!user) return;

    try {
      setLoadingData(true);
      
      // Загружаем полные данные пользователя через GET /:id для получения role_id и phone
      let fullUserData = null;
      try {
        const userResponse = await api.get(`/admin/users/${user.id}`);
        if (userResponse.data.success && userResponse.data.user) {
          fullUserData = userResponse.data.user;
        }
      } catch (error) {
        console.error('Ошибка загрузки полных данных пользователя:', error);
      }
      
      const subType = fullUserData?.subscriptionType ?? user.subscriptionType ?? 'free';
      const subEnd = fullUserData?.subscriptionEndDate ?? user.subscriptionEndDate;
      const rawStatus = fullUserData?.subscriptionStatus ?? user.subscriptionStatus ?? 'active';
      // Триал/сбой оплаты управляются платёжным флоу; в ручной форме админа их
      // приводим к 'active' (доступ есть), редактируемые статусы — active/expired/cancelled.
      const subStatus: 'active' | 'expired' | 'cancelled' =
        rawStatus === 'expired' || rawStatus === 'cancelled' ? rawStatus : 'active';

      setFormData({
        firstName: fullUserData?.firstName ?? user.firstName ?? '',
        lastName: fullUserData?.lastName ?? user.lastName ?? '',
        email: fullUserData?.email ?? user.email ?? '',
        phone: fullUserData?.phone ?? user.phone ?? '',
        password: '',
        subscriptionType: subType,
        subscriptionEndDate: subEnd ? new Date(subEnd).toISOString().split('T')[0] : '',
        subscriptionStatus: subStatus,
        autoRenew: fullUserData?.subscriptionAutoRenew ?? false
      });

      // Устанавливаем выбранную роль (дожидаемся загрузки ролей)
      if (roles.length > 0) {
        if (fullUserData?.roleId != null) {
          setSelectedRoleId(fullUserData.roleId);
        } else if (user.roleName) {
          const role = roles.find(r => r.name === user.roleName);
          if (role) {
            setSelectedRoleId(role.id);
          }
        }
      }

      // Загружаем историю платежей
      try {
        const paymentsResponse = await api.get(`/admin/users/${user.id}/payments`);
        if (paymentsResponse.data && paymentsResponse.data.success) {
          setPaymentHistory(paymentsResponse.data.payments || []);
        }
      } catch (error) {
        console.error('Ошибка загрузки истории платежей:', error);
        setPaymentHistory([]);
      }

      // Загружаем количество резюме
      try {
        const resumesResponse = await api.get(`/admin/users/${user.id}/resumes-count`);
        if (resumesResponse.data && resumesResponse.data.success) {
          setResumeCount(resumesResponse.data.count || 0);
        }
      } catch (error) {
        console.error('Ошибка загрузки количества резюме:', error);
        setResumeCount(0);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных пользователя:', error);
      toast.error('Ошибка загрузки данных пользователя');
    } finally {
      setLoadingData(false);
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setFormData({ ...formData, password: newPassword });
    setShowPassword(true);
    toast.success('Пароль сгенерирован');
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Валидация
      if (!formData.email) {
        toast.error('Email обязателен');
        return;
      }

      if ((formData.subscriptionType === 'premium' || formData.subscriptionType === 'standard') && !formData.subscriptionEndDate) {
        toast.error('Укажите дату окончания подписки для Standard и Premium');
        return;
      }

      // Обновляем данные пользователя
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || null
      };

      // Если пароль указан, обновляем его
      if (formData.password) {
        updateData.password = formData.password;
      }

      // Обновляем пользователя
      await api.put(`/admin/users/${user.id}`, updateData);

      // Обновляем подписку
      await api.put(`/admin/users/${user.id}/subscription`, {
        subscriptionType: formData.subscriptionType,
        expiresAt: formData.subscriptionEndDate || null,
        status: formData.subscriptionStatus,
        autoRenew: formData.autoRenew
      });

      // Обновляем роль, если она изменилась
      if (selectedRoleId !== null) {
        await api.put(`/admin/users/${user.id}/role`, { roleId: selectedRoleId });
      }

      toast.success('Данные пользователя обновлены');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Ошибка обновления пользователя:', error);
      toast.error(error.response?.data?.message || 'Ошибка обновления пользователя');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Редактирование пользователя</h2>
            <p className="text-sm text-gray-600 mt-1">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'info'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Основная информация
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'subscription'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Подписка
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'stats'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Статистика
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingData ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Таб: Основная информация */}
              {activeTab === 'info' && (
                <div className="space-y-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="h-4 w-4 inline mr-1" />
                      Имя
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Имя"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="h-4 w-4 inline mr-1" />
                      Фамилия
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Фамилия"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="h-4 w-4 inline mr-1" />
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="h-4 w-4 inline mr-1" />
                      Телефон
                    </label>
                    <input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+7 (999) 123-45-67"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Shield className="h-4 w-4 inline mr-1" />
                      Роль
                    </label>
                    <select
                      value={selectedRoleId || ''}
                      onChange={(e) => setSelectedRoleId(parseInt(e.target.value) || null)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={loadingRoles}
                    >
                      {loadingRoles ? (
                        <option>Загрузка ролей...</option>
                      ) : (
                        roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name} {role.description ? `- ${role.description}` : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Пароль
                    </label>
                    <div className="flex gap-2">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Оставьте пустым, чтобы не менять"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-600" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-600" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                        title="Сгенерировать пароль"
                      >
                        <Dice6 className="h-5 w-5 text-gray-600" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Оставьте пустым, чтобы не менять пароль</p>
                  </div>
                </div>
              )}

              {/* Таб: Подписка */}
              {activeTab === 'subscription' && (
                <div className="space-y-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Тип подписки
                    </label>
                    <select
                      value={formData.subscriptionType}
                      onChange={(e) => setFormData({ ...formData, subscriptionType: e.target.value as 'free' | 'standard' | 'premium' })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="free">Free</option>
                      <option value="standard">Standard</option>
                      <option value="premium">Premium</option>
                    </select>
                  </div>

                  {(formData.subscriptionType === 'premium' || formData.subscriptionType === 'standard') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Дата окончания подписки
                        </label>
                        <input
                          type="date"
                          value={formData.subscriptionEndDate}
                          onChange={(e) => setFormData({ ...formData, subscriptionEndDate: e.target.value })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Статус подписки
                        </label>
                        <select
                          value={formData.subscriptionStatus}
                          onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value as 'active' | 'expired' | 'cancelled' })}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="active">Активна</option>
                          <option value="expired">Истекла</option>
                          <option value="cancelled">Отменена</option>
                        </select>
                      </div>

                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.autoRenew}
                            onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            Автоматическое продление подписки
                          </span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1">
                          При включении подписка будет автоматически продлеваться при истечении срока
                        </p>
                      </div>
                    </>
                  )}

                  {/* История платежей */}
                  {paymentHistory.length > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">История платежей</h3>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {paymentHistory.map((payment, index) => {
                          // Определяем статус подписки
                          const getStatusText = () => {
                            if (payment.status === 'active') return 'Активна';
                            if (payment.status === 'expired') return 'Истекла';
                            if (payment.status === 'cancelled') return 'Отменена';
                            return payment.status || 'Неизвестно';
                          };

                          // Форматируем дату окончания
                          const getExpiryDate = () => {
                            if (!payment.expiresAt) return null;
                            return new Date(payment.expiresAt).toLocaleDateString('ru-RU');
                          };

                          return (
                            <div key={payment.id || index} className="p-3 bg-gray-50 rounded-lg text-sm">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {payment.paymentProvider || payment.grantedByAdminEmail || payment.grantedByAdminName || 'Платеж'}
                                  </div>
                                  <div className="text-gray-500 text-xs mt-1">
                                    {payment.createdAt ? new Date(payment.createdAt).toLocaleString('ru-RU') : '—'}
                                  </div>
                                  <div className="text-gray-900 font-semibold mt-1">
                                    {payment.amount !== null && payment.amount !== undefined 
                                      ? `${payment.amount.toFixed(2)} ₽` 
                                      : '—'}
                                  </div>
                                </div>
                                <div className="text-right ml-4">
                                  {payment.paymentId ? (
                                    <div className="font-medium text-gray-900 text-xs font-mono mb-2">
                                      ID: {payment.paymentId}
                                    </div>
                                  ) : null}
                                  <div className="text-xs">
                                    <div className="font-medium text-gray-900">
                                      {getStatusText()}
                                    </div>
                                    {payment.expiresAt ? (
                                      <div className="text-gray-500 mt-1">
                                        до {getExpiryDate()}
                                      </div>
                                    ) : (
                                      <div className="text-gray-400 mt-1">
                                        без ограничений
                                      </div>
                                    )}
                                  </div>
                                  {payment.subscriptionType && (
                                    <div className="text-xs text-gray-500 mt-2 capitalize">
                                      {payment.subscriptionType}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Таб: Статистика */}
              {activeTab === 'stats' && (
                <div className="space-y-4 max-w-2xl">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="h-6 w-6 text-gray-600 mr-3" />
                        <span className="text-base font-medium text-gray-700">Создано резюме</span>
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{resumeCount}</span>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <CreditCard className="h-6 w-6 text-gray-600 mr-3" />
                        <span className="text-base font-medium text-gray-700">Платежей</span>
                      </div>
                      <span className="text-2xl font-bold text-gray-900">{paymentHistory.length}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors"
            disabled={loading}
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={loading || loadingData}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Сохранение...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Сохранить
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;

